import { FaRobot, FaCookie } from "react-icons/fa6";
import { HiSparkles, HiShieldExclamation, HiCog6Tooth, HiMapPin, HiComputerDesktop, HiPuzzlePiece, HiInformationCircle } from "react-icons/hi2";
import { MdCleaningServices } from "react-icons/md";
import { FaFolder } from "react-icons/fa";

/**
 * Category Display Properties
 *
 * Defines how each category is displayed in the UI.
 * Categories are auto-derived from configuration items.
 */

// Set to false to use a single color (blue) for all categories
const USE_UNIQUE_COLORS = true;

const COLOR_CLASSES = {
  blue: {
    bg: "bg-blue-400/10",
    border: "border-blue-400/30",
    text: "text-blue-400",
    hoverBorder: "hover:border-blue-400/50",
    hex: "#60a5fa",
  },
  emerald: {
    bg: "bg-emerald-400/10",
    border: "border-emerald-400/30",
    text: "text-emerald-400",
    hoverBorder: "hover:border-emerald-400/50",
    hex: "#34d399",
  },
  violet: {
    bg: "bg-violet-400/10",
    border: "border-violet-400/30",
    text: "text-violet-400",
    hoverBorder: "hover:border-violet-400/50",
    hex: "#a78bfa",
  },
  cyan: {
    bg: "bg-cyan-400/10",
    border: "border-cyan-400/30",
    text: "text-cyan-400",
    hoverBorder: "hover:border-cyan-400/50",
    hex: "#22d3ee",
  },
  amber: {
    bg: "bg-amber-400/10",
    border: "border-amber-400/30",
    text: "text-amber-400",
    hoverBorder: "hover:border-amber-400/50",
    hex: "#fbbf24",
  },
  orange: {
    bg: "bg-orange-400/10",
    border: "border-orange-400/30",
    text: "text-orange-400",
    hoverBorder: "hover:border-orange-400/50",
    hex: "#fb923c",
  },
  rose: {
    bg: "bg-rose-400/10",
    border: "border-rose-400/30",
    text: "text-rose-400",
    hoverBorder: "hover:border-rose-400/50",
    hex: "#fb7185",
  },
  lime: {
    bg: "bg-lime-400/10",
    border: "border-lime-400/30",
    text: "text-lime-400",
    hoverBorder: "hover:border-lime-400/50",
    hex: "#a3e635",
  },
  indigo: {
    bg: "bg-indigo-400/10",
    border: "border-indigo-400/30",
    text: "text-indigo-400",
    hoverBorder: "hover:border-indigo-400/50",
    hex: "#818cf8",
  },
};

const getColor = (color) => USE_UNIQUE_COLORS ? COLOR_CLASSES[color] : COLOR_CLASSES.blue;

export const CATEGORY_DISPLAY = {
  general: {
    id: "general",
    label: "General",
    Icon: HiInformationCircle,
    description: "Task name and metadata",
    order: 0,
    colorClasses: getColor("indigo"),
  },
  core: {
    id: "core",
    label: "Automation",
    Icon: FaRobot,
    description: "Preview or edit your data extract automation",
    order: 1,
    colorClasses: getColor("blue"),
  },
  pageUnblocker: {
    id: "pageUnblocker",
    label: "Page Unblocker",
    Icon: HiShieldExclamation,
    description: "Handle popups, captchas, and access blocks",
    order: 3,
    colorClasses: getColor("rose"),
  },
  beforeRun: {
    id: "beforeRun",
    label: "Before Run",
    Icon: HiCog6Tooth,
    description: "Settings applied before extraction starts",
    order: 10,
    colorClasses: getColor("emerald"),
    disabled: true,
  },
  cookiesStorage: {
    id: "cookiesStorage",
    label: "Authentication & Cookies",
    Icon: FaCookie,
    description: "Pre-load cookies and localStorage into cloud browser",
    order: 4,
    colorClasses: getColor("amber"),
  },
  browserLocation: {
    id: "browserLocation",
    label: "Browser Location",
    Icon: HiMapPin,
    description: "Configure browser location and proxy",
    order: 2,
    colorClasses: getColor("cyan"),
  },
  userAgent: {
    id: "userAgent",
    label: "User Agent",
    Icon: HiComputerDesktop,
    description: "Configure browser user agent and viewport",
    order: 5,
    colorClasses: getColor("violet"),
  },
  afterRun: {
    id: "afterRun",
    label: "After Run",
    Icon: HiSparkles,
    description: "Post-processing and data optimization",
    order: 6,
    colorClasses: getColor("orange"),
  },
  dataCleaning: {
    id: "dataCleaning",
    label: "Data Cleanup",
    Icon: MdCleaningServices,
    description: "Clean and optimize extracted data",
    order: 7,
    colorClasses: getColor("lime"),
  },
  integrations: {
    id: "integrations",
    label: "Integrations",
    Icon: HiPuzzlePiece,
    description: "Google Sheets, Webhooks, and more",
    order: 8,
    colorClasses: getColor("indigo"),
  },
};

/**
 * Get category display info with fallback for unknown categories
 */
export const getCategoryDisplay = (categoryId) => {
  return CATEGORY_DISPLAY[categoryId] || {
    id: categoryId,
    label: categoryId.charAt(0).toUpperCase() + categoryId.slice(1),
    Icon: FaFolder,
    description: "",
    order: 99,
    colorClasses: COLOR_CLASSES.blue,
  };
};

/**
 * Get sorted categories based on their display order
 */
export const getSortedCategories = (categoryIds) => {
  return categoryIds
    .map((id) => getCategoryDisplay(id))
    .filter((cat) => !cat.disabled)
    .sort((a, b) => a.order - b.order);
};
